// One list of vendors shared by every project. The document id is the vendor name with case, spaces
// and punctuation removed, so "Lancer", "lancer " and "L.ancer" are one vendor.
import { collection, doc, onSnapshot, query, serverTimestamp, setDoc, writeBatch } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../firebase';
import { sameVendorName, vendorKey } from './materialReceiving';

export const VENDOR_COLLECTION = 'vendors';

/** Adds vendor names that are not in the shared list yet. Writing a name that is already there changes nothing but its timestamp. */
export async function ensureVendors(projectId: string, known: string[], names: string[]): Promise<void> {
  const fresh: string[] = [];
  for (const raw of names) {
    const name = String(raw || '').trim();
    if (!name || known.some((existing) => sameVendorName(existing, name)) || fresh.some((existing) => sameVendorName(existing, name))) continue;
    fresh.push(name);
  }
  if (!fresh.length) return;
  const batch = writeBatch(db);
  fresh.forEach((name) => batch.set(doc(db, VENDOR_COLLECTION, vendorKey(name)), { name, createdInProject: projectId, updatedAt: serverTimestamp() }, { merge: true }));
  await batch.commit();
}

/** Every vendor known to any project, alphabetical. */
export function useVendors(): string[] {
  const [names, setNames] = useState<string[]>([]);
  useEffect(() => onSnapshot(query(collection(db, VENDOR_COLLECTION)), (snapshot) => {
    setNames(snapshot.docs.map((item) => String((item.data() as any).name || '').trim()).filter(Boolean).sort((a, b) => a.localeCompare(b)));
  }, () => setNames([])), []);
  return names;
}

/** Every vendor's own carting agents (transporters) — fixed per vendor, unrelated to any material's
 * rate — keyed the same way as a vendor's own document. */
export function useVendorCartingAgents(): Record<string, string[]> {
  const [agentsByVendor, setAgentsByVendor] = useState<Record<string, string[]>>({});
  useEffect(() => onSnapshot(query(collection(db, VENDOR_COLLECTION)), (snapshot) => {
    const next: Record<string, string[]> = {};
    snapshot.docs.forEach((item) => {
      const agents = (item.data() as any).cartingAgents;
      if (Array.isArray(agents) && agents.length) next[item.id] = agents.map((agent) => String(agent || '').trim()).filter(Boolean);
    });
    setAgentsByVendor(next);
  }, () => setAgentsByVendor({})), []);
  return agentsByVendor;
}

/** Replaces a vendor's whole carting-agent list. */
export async function saveCartingAgents(vendorName: string, agents: string[]): Promise<void> {
  const name = vendorName.trim();
  if (!name) return;
  await setDoc(doc(db, VENDOR_COLLECTION, vendorKey(name)), { name, cartingAgents: agents, updatedAt: serverTimestamp() }, { merge: true });
}
